/* ============================================
   Proyectos — Índice (tabla resumen)
   Aterrizaje de "Proyectos" en el portal: una fila por proyecto con estado,
   avance, tareas, modelo de cobro y monto/consumo. Clic → detalle (/portal/proyectos/:id).
   Reemplaza al muro plano de tareas que había antes en /portal/tareas.
   ============================================ */

import { useState, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { usePortalCliente } from '@/hooks/usePortalCliente'
import { useApi } from '@/hooks/useApi'
import { useCachedData } from '@/hooks/useCachedData'
import { supabase } from '@/lib/supabase'
import { formatCurrency, formatDate } from '@/lib/utils'
import { inferModeloCobro, MODELO_LABEL, progresoProyecto, type ModeloCobro } from '@/lib/billing'
import type { Tarea, Proyecto, Cliente } from '@/lib/types'
import { Breadcrumbs } from '@/components/ui'
import { useCrumbInicio } from '@/lib/portal-base'

const estadoBadge: Record<string, { label: string; className: string }> = {
  activo: { label: 'Activo', className: 'badge-aceptada' },
  pausado: { label: 'Pausado', className: 'badge-enviada' },
  completado: { label: 'Completado', className: 'badge-borrador' },
  cancelado: { label: 'Cancelado', className: 'badge-rechazada' },
}

const modeloClass: Record<ModeloCobro, string> = {
  horas: 'pi-model--horas',
  proyecto: 'pi-model--proyecto',
  tarea: 'pi-model--tarea',
}

type SegFilter = 'todos' | 'activo' | 'pausado' | 'completado'

export default function ProyectosIndex() {
  const { activeClienteId: clienteId, afiliaciones } = usePortalCliente()
  const api = useApi()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  // El partner reutiliza esta página bajo /partner/proyectos.
  const esPartner = pathname.startsWith('/partner')
  const base = esPartner ? '/partner/proyectos' : '/portal/proyectos'
  const inicio = useCrumbInicio()
  const [seg, setSeg] = useState<SegFilter>('todos')
  const [search, setSearch] = useState('')

  // La carga es la de antes, envuelta en caché: al volver a la pestaña ya no se
  // pide nada. `clienteId` va en la clave porque el partner y el multi-cuenta
  // cambian de empresa sin recargar la app — sin él, una cuenta vería la lista
  // de la otra.
  const { data, isLoading: loading } = useCachedData(
    ['portal-proyectos', clienteId, esPartner],
    async () => {
      // Los proyectos que el hosting auto-genera al provisionar son plomería
      // interna; se excluyen para no ensuciar la vista del cliente.
      const hosting = await api.get<{ proyecto_id?: number | null }[]>('/api/hosting/mi-hosting').catch(() => [])
      const hostingIds = new Set(
        (Array.isArray(hosting) ? hosting : []).map(h => h.proyecto_id).filter((v): v is number => v != null)
      )
      // Solo columnas que el índice usa: NO traer campos internos (notas, github_repo).
      const columnas = 'id, nombre, descripcion, estado, fecha_inicio, fecha_fin_estimada, moneda, presupuesto, modelo_cobro, cliente_id'
      // El partner no viene acá a ver SUS proyectos —casi nunca tiene— sino el
      // trabajo de su cartera: es su módulo de Operaciones. El backend ya acota
      // a cartera + empresa propia (`scopePorCliente`), así que se pide sin
      // filtro y la empresa pasa a ser una columna de la fila; con el `.eq` del
      // portal la lista le salía vacía aunque sus clientes tuvieran proyectos.
      const { data: pData } = esPartner
        ? await supabase.from('proyectos').select(columnas)
        : await supabase.from('proyectos').select(columnas).eq('cliente_id', clienteId)
      const proyectos = ((pData ?? []) as unknown as Proyecto[]).filter(p => !hostingIds.has(p.id))
      // Los nombres de empresa para esa columna. `/api/clientes` devuelve la
      // cartera del partner; la empresa propia no está ahí y sale de las
      // afiliaciones del contacto.
      const empresas = esPartner
        ? await api.get<Cliente[]>('/api/clientes').catch(() => [] as Cliente[])
        : [] as Cliente[]
      const ids = proyectos.map(p => p.id)
      if (ids.length === 0) return { proyectos, tareas: [] as Tarea[], empresas }
      const { data: tData } = await supabase.from('tareas')
        .select('id, proyecto_id, estado, monto, horas_reales, tipo_cobro, factura_id, fecha_limite')
        .in('proyecto_id', ids)
      return { proyectos, tareas: (tData ?? []) as unknown as Tarea[], empresas }
    },
    { activo: !!clienteId },
  )
  const proyectos = data?.proyectos ?? []
  const tareas = data?.tareas ?? []

  const nombreEmpresa = useMemo(() => {
    const m = new Map<number, string>()
    for (const a of afiliaciones) m.set(a.cliente_id, a.empresa_nombre)
    for (const cli of data?.empresas ?? []) m.set(cli.id, cli.empresa || `Empresa #${cli.id}`)
    return m
  }, [afiliaciones, data?.empresas])

  // ─── Métricas por proyecto ───
  const rows = useMemo(() => {
    return proyectos.map(p => {
      const ts = tareas.filter(t => t.proyecto_id === p.id)
      const done = ts.filter(t => t.estado === 'completada').length
      const modelo = inferModeloCobro(p, ts)
      const horas = ts.reduce((s, t) => s + (t.horas_reales ?? 0), 0)
      const cotizado = ts.reduce((s, t) => s + (t.monto ?? 0), 0)
      const facturado = ts.filter(t => t.factura_id != null).reduce((s, t) => s + (t.monto ?? 0), 0)
      return {
        p, modelo, horas, cotizado, facturado,
        progreso: progresoProyecto(ts),
        tareasDone: done, tareasTotal: ts.length,
      }
    })
  }, [proyectos, tareas])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return rows.filter(r => {
      if (seg !== 'todos' && r.p.estado !== seg) return false
      if (!q) return true
      // Para el partner la empresa es parte de la fila, así que también busca
      // por ella: con tres clientes en la lista, "LibreOvo" es lo primero que
      // uno escribe.
      const empresa = esPartner && r.p.cliente_id ? nombreEmpresa.get(r.p.cliente_id) ?? '' : ''
      return r.p.nombre.toLowerCase().includes(q) || empresa.toLowerCase().includes(q)
    })
  }, [rows, seg, search, esPartner, nombreEmpresa])

  // ─── KPIs ───
  const kpis = useMemo(() => {
    const activos = proyectos.filter(p => p.estado === 'activo').length
    const abiertas = tareas.filter(t => t.estado !== 'completada').length
    const pendFacturar = tareas
      .filter(t => t.estado === 'completada' && t.factura_id == null)
      .reduce((s, t) => s + (t.monto ?? 0), 0)
    return { activos, total: proyectos.length, abiertas, pendFacturar }
  }, [proyectos, tareas])

  function amountCell(r: typeof rows[number]) {
    if (r.modelo === 'proyecto') {
      return (
        <div className="pi-amount">
          {formatCurrency(r.p.presupuesto ?? 0)}
          <small>presupuesto</small>
        </div>
      )
    }
    if (r.modelo === 'tarea') {
      return (
        <div className="pi-amount">
          {formatCurrency(r.facturado)}
          <small>de {formatCurrency(r.cotizado)} cotizado</small>
        </div>
      )
    }
    return (
      <div className="pi-amount">
        {r.horas}h
        <small>registradas</small>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="portal-loading-state">
        <div className="auth-spinner" style={{ margin: '0 auto 12px' }} />
        <p>Cargando proyectos...</p>
      </div>
    )
  }

  return (
    <div className="pi-layout">
      <div className="pi-head">
        <Breadcrumbs items={[inicio, { label: 'Proyectos' }]} />
      </div>

      {/* KPIs */}
      <div className="pi-kpis">
        <div className="pi-kpi">
          <span className="pi-kpi-label">Proyectos activos</span>
          <span className="pi-kpi-val">{kpis.activos}<small> / {kpis.total}</small></span>
        </div>
        <div className="pi-kpi">
          <span className="pi-kpi-label">Tareas abiertas</span>
          <span className="pi-kpi-val">{kpis.abiertas}</span>
        </div>
        <div className="pi-kpi">
          <span className="pi-kpi-label">Pendiente por facturar</span>
          <span className="pi-kpi-val">{formatCurrency(kpis.pendFacturar)}</span>
        </div>
      </div>

      {/* Toolbar */}
      <div className="pi-toolbar">
        <div className="pt-search">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
          <input type="text" className="pt-search-input" placeholder="Buscar proyecto..." value={search} onChange={e => setSearch(e.target.value)} />
          {search && <button className="pt-search-clear" onClick={() => setSearch('')}>✕</button>}
        </div>
        <div className="pt-filter-chips">
          {([['todos', 'Todos'], ['activo', 'Activos'], ['pausado', 'Pausados'], ['completado', 'Completados']] as [SegFilter, string][]).map(([v, label]) => (
            <button key={v} className={`pt-filter-chip ${seg === v ? 'active' : ''}`} onClick={() => setSeg(v)}>{label}</button>
          ))}
        </div>
      </div>

      {/* Tabla */}
      <div className="pi-table-wrap">
        {filtered.length === 0 ? (
          <div className="pt-fact-empty">
            <h3>Sin proyectos</h3>
            <p>No hay proyectos que coincidan con el filtro.</p>
          </div>
        ) : (
          <table className="pi-table">
            <thead>
              <tr>
                <th>Proyecto</th>
                {esPartner && <th>Empresa</th>}
                <th>Estado</th>
                <th className="pi-col-prog">Avance</th>
                <th>Tareas</th>
                <th>Cobro</th>
                <th className="pi-r">Monto / consumo</th>
                <th>Próximo vto.</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => {
                const eb = estadoBadge[r.p.estado] ?? estadoBadge.activo
                const prox = nextDue(tareas.filter(t => t.proyecto_id === r.p.id))
                return (
                  <tr key={r.p.id} className="pi-row" onClick={() => navigate(`${base}/${r.p.id}`)}>
                    <td>
                      <div className="pi-name">
                        <span className="pi-name-main">{r.p.nombre}</span>
                        {r.p.descripcion && <span className="pi-name-sub">{r.p.descripcion}</span>}
                      </div>
                    </td>
                    {esPartner && (
                      <td><span className="pi-mini">{r.p.cliente_id ? nombreEmpresa.get(r.p.cliente_id) ?? '—' : '—'}</span></td>
                    )}
                    <td><span className={`badge ${eb.className}`}>{eb.label}</span></td>
                    <td>
                      <div className="pi-prog">
                        <div className="pi-prog-bar"><i style={{ width: `${r.progreso}%` }} /></div>
                        <span className="pi-prog-pct">{r.progreso}%</span>
                      </div>
                    </td>
                    <td><span className="pi-mini">{r.tareasDone}/{r.tareasTotal}</span></td>
                    <td><span className={`pi-model ${modeloClass[r.modelo]}`}>{MODELO_LABEL[r.modelo]}</span></td>
                    <td className="pi-r">{amountCell(r)}</td>
                    <td><span className="pi-mini">{prox ? formatDate(prox) : '—'}</span></td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

/** Fecha límite más próxima entre las tareas no completadas. */
function nextDue(tareas: Tarea[]): string | null {
  const fechas = tareas
    .filter(t => t.estado !== 'completada' && t.fecha_limite)
    .map(t => t.fecha_limite as string)
    .sort()
  return fechas[0] ?? null
}
